import React, { useState } from 'react';
import apiService, { errorHelpers, validationHelpers } from '../../../utils/apiService';

const UserProfile = ({ user }) => {
    const [formData, setFormData] = useState({
        firstname: user.firstname || '',
        middlename: user.middlename || '',
        lastname: user.lastname || '',
        phone: user.phone || '',
        address: user.address || ''
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateProfileForm = () => {
        const newErrors = {};

        if (!formData.firstname.trim()) {
            newErrors.firstname = 'First name is required';
        }

        if (!formData.lastname.trim()) {
            newErrors.lastname = 'Last name is required';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!validationHelpers.validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordForm = () => {
        const newErrors = {};

        if (!passwordData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!passwordData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else {
            const passwordValidation = validationHelpers.validatePassword(passwordData.newPassword);
            if (!passwordValidation.isValid) {
                const errorMessages = Object.values(passwordValidation.errors).filter(Boolean);
                newErrors.newPassword = errorMessages[0];
            }
        }

        if (!passwordData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your new password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();

        if (!validateProfileForm()) {
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            await apiService.auth.updateProfile(formData);

            setAlert({
                type: 'success',
                message: 'Profile updated successfully!'
            });

            // Update localStorage with new user data
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const updatedUser = { ...currentUser, ...formData };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // Auto-hide success message after 3 seconds
            setTimeout(() => setAlert(null), 3000);

        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (!validatePasswordForm()) {
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            await apiService.auth.changePassword({
                current_password: passwordData.currentPassword,
                new_password: passwordData.newPassword
            });

            setAlert({
                type: 'success',
                message: 'Password changed successfully!'
            });

            // Clear password form
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

            // Auto-hide success message after 3 seconds
            setTimeout(() => setAlert(null), 3000);

        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-profile-container">
            {/* Header */}
            <div className="profile-header">
                <div className="header-icon">👤</div>
                <h1>Profile Settings</h1>
                <p>Manage your account information and security settings</p>
            </div>

            {/* Alert */}
            {alert && (
                <div className={`alert alert-${alert.type}`}>
                    <span className="alert-icon">
                        {alert.type === 'error' && '⚠️'}
                        {alert.type === 'success' && '✅'}
                        {alert.type === 'info' && 'ℹ️'}
                    </span>
                    <span className="alert-message">{alert.message}</span>
                    <button
                        onClick={() => setAlert(null)}
                        className="alert-close"
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="profile-tabs">
                <button
                    className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    <span className="tab-icon">📝</span>
                    Profile Information
                </button>
                <button
                    className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}
                >
                    <span className="tab-icon">🔒</span>
                    Change Password
                </button>
            </div>

            {/* Content */}
            <div className="profile-content">
                {/* Profile Information Tab */}
                {activeTab === 'profile' && (
                    <div className="profile-step">
                        <div className="step-header">
                            <div className="step-icon">📝</div>
                            <h2>Profile Information</h2>
                            <p>Update your personal information below</p>
                        </div>

                        <div className="profile-form">
                            {/* Name Fields Row */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        First Name <span className="required">*</span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-prefix">👤</span>
                                        <input
                                            type="text"
                                            name="firstname"
                                            className={`form-input ${errors.firstname ? 'error' : ''}`}
                                            value={formData.firstname}
                                            onChange={handleProfileChange}
                                            disabled={loading}
                                            placeholder="Enter your first name"
                                        />
                                    </div>
                                    {errors.firstname && (
                                        <div className="form-error">{errors.firstname}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Last Name <span className="required">*</span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-prefix">👤</span>
                                        <input
                                            type="text"
                                            name="lastname"
                                            className={`form-input ${errors.lastname ? 'error' : ''}`}
                                            value={formData.lastname}
                                            onChange={handleProfileChange}
                                            disabled={loading}
                                            placeholder="Enter your last name"
                                        />
                                    </div>
                                    {errors.lastname && (
                                        <div className="form-error">{errors.lastname}</div>
                                    )}
                                </div>
                            </div>

                            {/* Middle Name */}
                            <div className="form-group">
                                <label className="form-label">Middle Name (Optional)</label>
                                <div className="input-group">
                                    <span className="input-prefix">👤</span>
                                    <input
                                        type="text"
                                        name="middlename"
                                        className="form-input"
                                        value={formData.middlename}
                                        onChange={handleProfileChange}
                                        disabled={loading}
                                        placeholder="Enter your middle name"
                                    />
                                </div>
                            </div>

                            {/* Email Field (Read-only) */}
                            <div className="form-group">
                                <label className="form-label">Email Address</label>
                                <div className="input-group">
                                    <span className="input-prefix">📧</span>
                                    <input
                                        type="email"
                                        className="form-input disabled"
                                        value={user.email}
                                        disabled
                                    />
                                </div>
                                <div className="input-help">Email cannot be changed</div>
                            </div>

                            {/* Phone Field */}
                            <div className="form-group">
                                <label className="form-label">
                                    Phone Number <span className="required">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-prefix">📱</span>
                                    <input
                                        type="tel"
                                        name="phone"
                                        className={`form-input ${errors.phone ? 'error' : ''}`}
                                        value={formData.phone}
                                        onChange={handleProfileChange}
                                        disabled={loading}
                                        placeholder="+234 801 234 5678"
                                    />
                                </div>
                                {errors.phone && (
                                    <div className="form-error">{errors.phone}</div>
                                )}
                            </div>

                            {/* Address Field */}
                            <div className="form-group">
                                <label className="form-label">
                                    Address <span className="required">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-prefix">📍</span>
                                    <textarea
                                        name="address"
                                        className={`form-textarea ${errors.address ? 'error' : ''}`}
                                        value={formData.address}
                                        onChange={handleProfileChange}
                                        disabled={loading}
                                        rows="3"
                                        placeholder="Enter your complete address"
                                    />
                                </div>
                                {errors.address && (
                                    <div className="form-error">{errors.address}</div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={handleProfileSubmit}
                                className="btn btn-primary btn-large"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <span className="btn-icon">💾</span>
                                        Update Profile
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Change Password Tab */}
                {activeTab === 'password' && (
                    <div className="profile-step">
                        <div className="step-header">
                            <div className="step-icon">🔒</div>
                            <h2>Change Password</h2>
                            <p>Update your password to keep your account secure</p>
                        </div>

                        <div className="password-form">
                            {/* Current Password */}
                            <div className="form-group">
                                <label className="form-label">
                                    Current Password <span className="required">*</span>
                                </label>
                                <div className="password-input">
                                    <span className="input-prefix">🔒</span>
                                    <input
                                        type={showPasswords.current ? 'text' : 'password'}
                                        name="currentPassword"
                                        className={`form-input ${errors.currentPassword ? 'error' : ''}`}
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        disabled={loading}
                                        placeholder="Enter your current password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => togglePasswordVisibility('current')}
                                    >
                                        {showPasswords.current ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.currentPassword && (
                                    <div className="form-error">{errors.currentPassword}</div>
                                )}
                            </div>

                            {/* New Password */}
                            <div className="form-group">
                                <label className="form-label">
                                    New Password <span className="required">*</span>
                                </label>
                                <div className="password-input">
                                    <span className="input-prefix">🔒</span>
                                    <input
                                        type={showPasswords.new ? 'text' : 'password'}
                                        name="newPassword"
                                        className={`form-input ${errors.newPassword ? 'error' : ''}`}
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        disabled={loading}
                                        placeholder="Enter your new password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => togglePasswordVisibility('new')}
                                    >
                                        {showPasswords.new ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.newPassword && (
                                    <div className="form-error">{errors.newPassword}</div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div className="form-group">
                                <label className="form-label">
                                    Confirm New Password <span className="required">*</span>
                                </label>
                                <div className="password-input">
                                    <span className="input-prefix">🔒</span>
                                    <input
                                        type={showPasswords.confirm ? 'text' : 'password'}
                                        name="confirmPassword"
                                        className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        disabled={loading}
                                        placeholder="Confirm your new password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => togglePasswordVisibility('confirm')}
                                    >
                                        {showPasswords.confirm ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <div className="form-error">{errors.confirmPassword}</div>
                                )}
                            </div>

                            {/* Password Requirements */}
                            <div className="info-card">
                                <div className="info-header">
                                    <span className="info-icon">ℹ️</span>
                                    <h4>Password Requirements</h4>
                                </div>
                                <div className="requirements-list">
                                    <div className="requirement-item">
                                        <span className="requirement-icon">✓</span>
                                        <span>At least 8 characters long</span>
                                    </div>
                                    <div className="requirement-item">
                                        <span className="requirement-icon">✓</span>
                                        <span>Contains uppercase letter</span>
                                    </div>
                                    <div className="requirement-item">
                                        <span className="requirement-icon">✓</span>
                                        <span>Contains lowercase letter</span>
                                    </div>
                                    <div className="requirement-item">
                                        <span className="requirement-icon">✓</span>
                                        <span>Contains at least one number</span>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={handlePasswordSubmit}
                                className="btn btn-primary btn-large"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Changing Password...
                                    </>
                                ) : (
                                    <>
                                        <span className="btn-icon">🔐</span>
                                        Change Password
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .user-profile-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 1rem;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                .profile-header {
                    text-align: center;
                    margin-bottom: 2rem;
                    padding: 3rem 2rem;
                    background: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
                    border-radius: 16px;
                    color: white;
                    box-shadow: 0 10px 40px rgba(20, 184, 166, 0.3);
                }

                .header-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    display: block;
                }

                .profile-header h1 {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0 0 1rem 0;
                }

                .profile-header p {
                    font-size: 1.125rem;
                    margin: 0;
                    opacity: 0.9;
                }

                .alert {
                    display: flex;
                    align-items: center;
                    padding: 1rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                    position: relative;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .alert-error {
                    background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
                    border: 1px solid #f472b6;
                    color: #c026d3;
                }

                .alert-success {
                    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
                    border: 1px solid #34d399;
                    color: #047857;
                }

                .alert-info {
                    background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%);
                    border: 1px solid #5eead4;
                    color: #0d9488;
                }

                .alert-icon {
                    font-size: 1.25rem;
                    margin-right: 0.75rem;
                }

                .alert-message {
                    flex: 1;
                    font-weight: 500;
                }

                .alert-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: inherit;
                    opacity: 0.7;
                    transition: opacity 0.2s;
                }

                .alert-close:hover {
                    opacity: 1;
                }

                .profile-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 2rem;
                    background: white;
                    padding: 0.5rem;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
                }

                .tab-button {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1rem 1.5rem;
                    border: none;
                    background: transparent;
                    color: #6b7280;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }

                .tab-button:hover {
                    background: #f8fafc;
                    color: #374151;
                }

                .tab-button.active {
                    background: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
                    color: white;
                    box-shadow: 0 4px 15px rgba(20, 184, 166, 0.4);
                }

                .tab-icon {
                    font-size: 1.125rem;
                }

                .profile-content {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .profile-step {
                    padding: 3rem 2rem;
                    text-align: center;
                }

                .step-header {
                    margin-bottom: 2.5rem;
                }

                .step-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    display: block;
                }

                .step-header h2 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 0.75rem;
                }

                .step-header p {
                    font-size: 1.125rem;
                    color: #6b7280;
                    margin: 0;
                }

                .profile-form,
                .password-form {
                    text-align: left;
                    max-width: 600px;
                    margin: 0 auto;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                    margin-bottom: 1.5rem;
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-label {
                    display: block;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }

                .required {
                    color: #dc2626;
                }

                .input-group {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .input-prefix {
                    position: absolute;
                    left: 1rem;
                    font-size: 1.25rem;
                    z-index: 1;
                }

                .form-input {
                    width: 100%;
                    padding: 1rem 1rem 1rem 3rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                    background: #f9fafb;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #14b8a6;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
                }

                .form-input.error {
                    border-color: #ef4444;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
                }

                .form-input.disabled {
                    background: #f3f4f6;
                    color: #6b7280;
                    cursor: not-allowed;
                }

                .form-input:disabled {
                    background: #f3f4f6;
                    color: #9ca3af;
                    cursor: not-allowed;
                }

                .form-textarea {
                    width: 100%;
                    padding: 1rem 1rem 1rem 3rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                    background: #f9fafb;
                    resize: vertical;
                    min-height: 100px;
                    font-family: inherit;
                    box-sizing: border-box;
                }

                .form-textarea:focus {
                    outline: none;
                    border-color: #14b8a6;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
                }

                .form-textarea.error {
                    border-color: #ef4444;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
                }

                .password-input {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .password-toggle {
                    position: absolute;
                    right: 1rem;
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    padding: 0.25rem;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                    z-index: 2;
                }

                .password-toggle:hover {
                    background: #f1f5f9;
                }

                .form-error {
                    color: #ef4444;
                    font-size: 0.875rem;
                    margin-top: 0.5rem;
                    font-weight: 500;
                }

                .input-help {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin-top: 0.5rem;
                }

                .info-card {
                    background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%);
                    border: 1px solid #5eead4;
                    border-radius: 16px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .info-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .info-icon {
                    font-size: 1.5rem;
                    margin-right: 0.75rem;
                }

                .info-header h4 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #0d9488;
                    margin: 0;
                }

                .requirements-list {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 0.75rem;
                }

                .requirement-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem;
                    background: rgba(255, 255, 255, 0.7);
                    border-radius: 8px;
                    font-size: 0.875rem;
                    color: #0d9488;
                }

                .requirement-icon {
                    color: #056762;
                    font-weight: bold;
                }

                .btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 12px;
                    font-size: 1rem;
                    font-weight: 600;
                    text-decoration: none;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    min-height: 48px;
                    width: 100%;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-large {
                    padding: 1rem 2rem;
                    font-size: 1.125rem;
                    min-height: 56px;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #14b8a6 0%, #10b981 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
                }

                .btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #0f766e 0%, #117e65 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(20, 184, 166, 0.4);
                }

                .btn-icon {
                    font-size: 1.125rem;
                }

                .spinner {
                    display: inline-block;
                    width: 1rem;
                    height: 1rem;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .user-profile-container {
                        padding: 0.5rem;
                    }

                    .profile-header {
                        padding: 2rem 1rem;
                    }

                    .profile-header h1 {
                        font-size: 2rem;
                    }

                    .header-icon {
                        font-size: 3rem;
                    }

                    .profile-step {
                        padding: 2rem 1rem;
                    }

                    .step-header h2 {
                        font-size: 1.5rem;
                    }

                    .step-header p {
                        font-size: 1rem;
                    }

                    .step-icon {
                        font-size: 3rem;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                        gap: 1rem;
                    }

                    .profile-tabs {
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .tab-button {
                        text-align: left;
                        justify-content: flex-start;
                    }

                    .requirements-list {
                        grid-template-columns: 1fr;
                    }

                    .profile-form,
                    .password-form {
                        max-width: 100%;
                    }

                    .form-input,
                    .form-textarea {
                        font-size: 16px; /* Prevents zoom on iOS */
                    }
                }

                /* Dark mode support */
                @media (prefers-color-scheme: dark) {
                    .user-profile-container {
                        background: #0f172a;
                        color: #e2e8f0;
                    }

                    .profile-content {
                        background: #1e293b;
                        border: 1px solid #334155;
                    }

                    .step-header h2 {
                        color: #f1f5f9;
                    }

                    .step-header p {
                        color: #cbd5e1;
                    }

                    .form-label {
                        color: #f1f5f9;
                    }

                    .form-input,
                    .form-textarea {
                        background: #334155;
                        border-color: #475569;
                        color: #e2e8f0;
                    }

                    .form-input:focus,
                    .form-textarea:focus {
                        background: #334155;
                        border-color: #14b8a6;
                    }

                    .form-input.disabled {
                        background: #374151;
                        color: #9ca3af;
                    }

                    .profile-tabs {
                        background: #1e293b;
                        border: 1px solid #334155;
                    }

                    .tab-button {
                        color: #cbd5e1;
                    }

                    .tab-button:hover {
                        background: #334155;
                        color: #e2e8f0;
                    }

                    .info-card {
                        background: linear-gradient(135deg, #134e4a 0%, #0d9488 100%);
                        border-color: #14b8a6;
                    }

                    .info-header h4 {
                        color: #ccfbf1;
                    }

                    .requirement-item {
                        background: rgba(20, 184, 166, 0.1);
                        color: #ccfbf1;
                    }

                    .requirement-icon {
                        color: #14b8a6;
                    }
                }

                /* Focus styles for accessibility */
                .btn:focus-visible {
                    outline: 2px solid #14b8a6;
                    outline-offset: 2px;
                }

                .tab-button:focus-visible {
                    outline: 2px solid #14b8a6;
                    outline-offset: 2px;
                    border-radius: 8px;
                }

                .password-toggle:focus-visible {
                    outline: 2px solid #14b8a6;
                    outline-offset: 2px;
                }

                /* Smooth transitions for better UX */
                * {
                    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
                }

                /* Loading state enhancements */
                .btn:disabled {
                    position: relative;
                    overflow: hidden;
                }

                .btn:disabled::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
                    animation: loading-shimmer 1.5s infinite;
                }

                @keyframes loading-shimmer {
                    0% { left: -100%; }
                    100% { left: 100%; }
                }

                /* Enhanced form validation styles */
                .form-input.error {
                    animation: shake 0.3s ease-in-out;
                }

                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }

                /* Success animation for alerts */
                .alert-success {
                    animation: slideInFromTop 0.3s ease-out;
                }

                @keyframes slideInFromTop {
                    0% {
                        transform: translateY(-20px);
                        opacity: 0;
                    }
                    100% {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                /* Improved touch targets for mobile */
                @media (max-width: 768px) {
                    .btn,
                    .tab-button,
                    .password-toggle {
                        min-height: 44px; /* iOS recommended touch target */
                    }

                    .alert-close {
                        min-width: 44px;
                        min-height: 44px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                }

                /* Print styles */
                @media print {
                    .user-profile-container {
                        background: white;
                        box-shadow: none;
                    }

                    .profile-header {
                        background: #f8fafc !important;
                        color: #1f2937 !important;
                        box-shadow: none;
                    }

                    .profile-content {
                        box-shadow: none;
                        border: 1px solid #e5e7eb;
                    }

                    .btn,
                    .alert-close,
                    .password-toggle {
                        display: none;
                    }

                    .profile-tabs {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default UserProfile;
